import { DEFAULT_PAGE_SIZE, DEFAULT_FILTERS, FILTER_KEYS } from './constants';
import { normalizeDeviceType } from '../utils/deviceTypes';

const LIST_PARAM_KEYS = new Set(['page', 'page_size', ...FILTER_KEYS]);

export const parsePositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const getInitialFilters = (searchParams) => FILTER_KEYS.reduce((accumulator, key) => {
  accumulator[key] = searchParams.get(key) || '';
  return accumulator;
}, { ...DEFAULT_FILTERS });

export const getInitialPagination = (searchParams) => {
  const page = parsePositiveInt(searchParams.get('page'), 1);
  const pageSize = parsePositiveInt(searchParams.get('page_size'), DEFAULT_PAGE_SIZE);
  return {
    pageIndex: Math.max(page - 1, 0),
    pageSize,
  };
};

export const buildListParams = (filters, pagination) => {
  const params = new URLSearchParams();
  params.set('page', String(pagination.pageIndex + 1));
  params.set('page_size', String(pagination.pageSize));

  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== '') {
      params.set(key, String(value).trim());
    }
  });

  return params;
};

export const buildListUrlParams = (filters, pagination, currentSearch = '') => {
  const params = new URLSearchParams(currentSearch);

  LIST_PARAM_KEYS.forEach((key) => {
    params.delete(key);
  });

  buildListParams(filters, pagination).forEach((value, key) => {
    params.set(key, value);
  });

  return params;
};

export const getErrorMessage = (error, fallbackMessage) => {
  const errorData = error?.response?.data;
  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData;
  }

  if (typeof errorData?.error === 'string' && errorData.error.trim()) {
    return errorData.error;
  }

  if (typeof errorData?.detail === 'string' && errorData.detail.trim()) {
    return errorData.detail;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

export const formatScalar = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }

  return String(value);
};

export const formatJson = (value) => {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

export const buildDetailPath = (snapshotId, search = '') => `/wivi_inventory/${snapshotId}${search || ''}`;

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === null || bytes === undefined || bytes === '') return null;
  const numBytes = Number(bytes);
  if (Number.isNaN(numBytes)) return bytes;
  if (numBytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  
  return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

  const getFirstDefinedValue = (object, candidatePaths) => {
    for (const path of candidatePaths) {
      const value = path.split('.').reduce((current, segment) => current?.[segment], object);
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }

    return null;
  };

  export const getInventoryDeviceType = (snapshot) => {
    const rawValue = getFirstDefinedValue(snapshot, [
      'device_type',
      'remote_device_type',
      'local_device.device_type',
    ]);

    return normalizeDeviceType(rawValue);
  };

  export const getOnStarLastSeen = (snapshot) => getFirstDefinedValue(snapshot, [
    'onstar_last_seen',
    'local_vehicle.onstar_last_seen',
  ]);

  export const getInventoryLastUpdated = (snapshot) => getFirstDefinedValue(snapshot, [
    'last_updated',
    'status_payload.online.lastUpdated',
    'status_payload.logging.lastUpdated',
    'status_payload.running.lastUpdated',
    'status_payload.signalBars.lastUpdated',
    'status_payload.networkType.lastUpdated',
    'status_payload.totalStorage.lastUpdated',
    'status_payload.remainingStorage.lastUpdated',
    'raw_device_payload.status.online.lastUpdated',
    'raw_device_payload.status.logging.lastUpdated',
    'raw_device_payload.status.running.lastUpdated',
    'raw_device_payload.status.signalBars.lastUpdated',
    'raw_device_payload.status.networkType.lastUpdated',
    'merged_payload.resolved_device.status.online.lastUpdated',
    'merged_payload.resolved_device.status.logging.lastUpdated',
    'merged_payload.resolved_device.status.running.lastUpdated',
    'merged_payload.resolved_device.status.signalBars.lastUpdated',
    'merged_payload.resolved_device.status.networkType.lastUpdated',
  ]);

  export const getInventoryClientVersion = (snapshot) => getFirstDefinedValue(snapshot, [
    'client_version',
    'device_versions.client_version',
    'device_versions.clientVersion',
    'raw_device_payload.client_version',
    'raw_device_payload.clientVersion',
    'raw_device_payload.device.client_version',
    'raw_device_payload.device.clientVersion',
    'merged_payload.resolved_device.client_version',
    'merged_payload.resolved_device.clientVersion',
    'merged_payload.resolved_device.device.client_version',
    'merged_payload.resolved_device.device.clientVersion',
  ]);

  export const formatDistanceValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not available';
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue.toLocaleString();
    }

    return String(value);
  };
